// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## processRhythm.js
//
// Examines a voice (uTheory.Notation.Voice) and determines and logs
// the rhythm skills that are found in the rhythm of that voice
import { meterSimpleCutTimeDictionary } from 'shared/definitions/musicTheory/rhythms';
import type { Skill } from 'definitions/skill-definitions';
import { skills as skillsList } from 'shared/src/knowledge/knowledgeGraphNodes';
import utils from '../utils';
import TimeSignature from '../uTheory.Notation/timeSignature';
import type Voice from '../uTheory.Notation/voice';
import type Pointer from '../uTheory.Notation/pointer';
import type { NotePointers } from '../uTheory.Notation/measure';

/** Skills represented */
export interface RhythmSkillsGroup extends Skill {
  id: null | number;
  topicId: null | number;
  areaId: null | number;
  title: null | Skill['title'];
  master: null | { accuracy: number };
  advance: null | { accuracy: number };
  textId: string;
  rhythm: string;
  simple: boolean;
  cutTime: boolean;
  numberOfNotes: number;
  numberOfSoundedNotes: number;
  startPointer: Pointer;
}

/**
 * Process a rhythm to determine skills and tempo range.
 * @param {Voice} voice a uTheory.Notation.Voice object
 */
const processRhythm = (voice: Voice, ts: TimeSignature = voice.music.getTimeSignature(0)) => {
  if (!(ts instanceof TimeSignature)) {
    throw new Error('NaT: A time signature is required to process a rhythm.');
  }

  const tsData = meterSimpleCutTimeDictionary[ts.toString()];
  if (typeof tsData === 'undefined') {
    throw new Error(`NaT: Unable to find time signature name ${ts} in data table.`);
  }

  const skills = skillsList.filter(
    (skill) =>
      skill.areaId === 2 && // rhythm skills
      skill.simple === tsData.simple && // simple or compound?
      skill.cutTime === tsData.cutTime // cut time skills?
  );

  // Pull the pointers from the voice:
  const pointers = voice.getPointers();
  // Chunk pointers by groups that occupy a complete beat, or multiple
  // complete beats -- but not partial beats.  These will effectively
  // match up to the rhythmic skills, which are all beat length or
  // multiple beat length (non-divisible) units.

  // grouped by beat, from original voice pointers
  const pointerGroups: NotePointers[][] = pointers.reduce((groups, pointer) => {
    const currentGroup = groups[groups.length - 1];
    // If the subdivision isn't 0, and there is a current group, add it to the current group:
    if (pointer.startPointer.s !== 0 && currentGroup) currentGroup.push(pointer);
    else groups.push([pointer]);
    return groups;
  }, []);

  // Helper function to find a skill:
  const searchSkills = (str) => skills.find((skill) => skill.rhythm === str);

  // Keep each found group in skillsGroups:
  const skillsGroups: RhythmSkillsGroup[] = [];

  pointerGroups.forEach((group) => {
    // Make a string for the rhythm of the group.  Leave off initial ties.
    let str = '';

    for (let i = 0; i < group.length; i++) {
      const rhythm = group[i].noteHolder.rhythm;
      let baseDuration: string = rhythm.baseDuration;
      // Adjust for 8 vs e and 16 vs s.
      if (utils.isNumeric(baseDuration)) {
        const bd = Number(baseDuration);
        if (bd === 8) baseDuration = 'e';
        if (bd === 16) baseDuration = 's';
      }
      // Construct the string:
      str += baseDuration;
      for (let j = 0; j < rhythm.dots; j++) str += '.';
      if (rhythm.isTied && i !== 0) str += 't';
      if (group[i].noteHolder.length === 0) {
        str += 'r';
      }
    }

    const numberOfSoundedNotes = group
      .map((g) => g.noteHolder)
      .filter((nh) => !(nh.rhythm.isTied || nh.isEmpty())).length;

    // Search skills for that string & add it to processedRhythm[]
    // console.log(`Group: ${ str }`);
    const skill = searchSkills(str);
    if (skill) {
      const clone: RhythmSkillsGroup = {
        startPointer: group[0].startPointer.clone(),
        numberOfNotes: group.length,
        numberOfSoundedNotes,
        rhythm: '',
        simple: true,
        cutTime: false,
        ...utils.clone(skill),
      };
      skillsGroups.push(clone);
    } else {
      console.log(`Skill not found: "${str}"`);
      console.log(voice.toString());
      let tsType = tsData.simple ? 'S:' : 'C:';
      tsType = tsData.cutTime ? 'H:' : tsType;

      skillsGroups.push({
        id: null,
        areaId: null,
        topicId: null,
        title: null,
        master: null,
        advance: null,
        minQuestions: 0,
        textId: tsType + str,
        rhythm: str,
        simple: tsData.simple,
        cutTime: tsData.cutTime,
        numberOfNotes: group.length,
        numberOfSoundedNotes,
        startPointer: group[0].startPointer.clone(),
      });
    }
  });

  // Return the matches:
  return skillsGroups;
};

export default processRhythm;
